import { Link } from 'react-router-dom';
import LoadingAnimation from '../LoadingAnimation';
import { classNames } from '../../helper/classNames';

type CommonProps = {
  children: React.ReactNode;
  disabled?: boolean;
  isSmall?: boolean;
  testId?: string;
  className?: string;
  variant?: 'primary' | 'secondary';
};

type ConditionalProps =
  | {
      type?: 'button';
      // "unknown" forces onClick function arguments to be casted to "unknown" manually, to avoid usage of "any" ESLint suggests  "Use `never` instead, this is useful when instantiating generic type parameters that you don't need to know the type of."
      onClick: (...arg: never[]) => void;
      to?: never;
      isLoading?: boolean;
    }
  | { type?: 'submit'; onClick?: never; to?: never; isLoading?: boolean }
  | { type: 'link'; onClick?: never; to: string; isLoading?: never };

type Props = CommonProps & ConditionalProps;

export const Button = ({
  children,
  disabled = false,
  isLoading = false,
  isSmall = false,
  onClick,
  testId,
  to,
  type = 'button',
  className,
  variant = 'primary',
}: Props) => {
  const Component = type === 'link' ? Link : 'button';
  const buttonProps = { onClick, type };
  const linkProps = { to };

  if (isLoading)
    return (
      <div className={'grid place-content-center'}>
        <LoadingAnimation size="sm" />
      </div>
    );

  return (
    // @ts-expect-error TS is complaining about the type
    <Component
      className={classNames(
        'inline-flex items-center justify-center rounded-xl border-[0.5px] px-5 py-2.5 shadow-lg transition-shadow duration-300 select-none',
        variant === 'secondary'
          ? `bg-gray-100 text-gray-900 border-none ${!disabled && 'desktop-hover:bg-gray-200 active:!bg-gray-300'}`
          : `border-orange-500/70 bg-orange-400 text-white ${!disabled && 'desktop-hover:bg-orange-500 active:!bg-orange-600'}`,
        disabled ? 'opacity-80' : 'active:shadow-inner',
        isSmall ? '' : 'w-56',
        className
      )}
      disabled={disabled}
      {...(type === 'button' ? buttonProps : linkProps)}
      data-testid={testId}
    >
      {children}
    </Component>
  );
};
